import React, { useCallback, useEffect, useState } from 'react';
import {
  icons,
  Menu,
  MenuItem,
  Separator,
  theme,
} from '@ublend-npm/aulaui-next';
import { Editor, Location, Node, Range, Element, Transforms } from 'slate';
import copy from 'copy-to-clipboard';
import { ReactEditor, useSlateStatic } from 'slate-react';

import { removeLink } from '../../lib/commands';
import { insertBlockLink } from '../../plugins/blockLink/blockLink';
import { useAulaEditorContext } from '../../EditorContext';
import { computePosition } from '../../lib/position';
import { isInlineLink } from '../../plugins/inlineLink/inlineLinkType';

// The max amount of characters to display in the 'secondaryText' area of the
// inline link menu, if a url is available:
const elementUrlMaxLengthChars = 72;

// Clamps the length of a string to the given maxLength, appending a suffix to
// indicate content was cut out, mimicking CSS's text-overflow: ellipsis property
const clampToLength = (
  source: string | undefined,
  maxLength: number,
  suffix = '...',
): string | undefined => {
  if (typeof source === 'undefined') return source;

  return source.length > maxLength
    ? `${source.substr(0, maxLength - suffix.length)}${suffix}`
    : source;
};

export const InlineLink = ({ onClose }: { onClose: () => void }) => {
  const editor = useSlateStatic();
  const { selection } = editor;
  const context = useAulaEditorContext();
  const [element, setElement] = useState<Node | null>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const elementUrl: string = (Element.isElement(element) && element.url) || '';
  const [copyLinklabel, setCopyLinkLabel] = useState('Copy link');

  const copyLink = useCallback(() => {
    copy(elementUrl);
    setCopyLinkLabel('Link copied');
    ReactEditor.focus(editor);
  }, [editor, elementUrl]);

  /**
   * Update element
   */
  useEffect(() => {
    if (!selection) {
      return;
    }
    const [elementInner] = Editor.nodes(editor, {
      at: selection as Location,
      match: isInlineLink as (node: Node) => boolean,
    });

    if (!elementInner) {
      return;
    }

    setElement(elementInner[0]);
  }, [editor, selection]);

  /**
   * Update position
   */
  useEffect(() => {
    if (!element) {
      return;
    }

    const computedPosition = computePosition();
    setPosition({
      top: computedPosition.top + 20,
      left: computedPosition.left - 50,
    });
  }, [element]);

  const expandLink = useCallback(() => {
    if (!element) {
      return;
    }
    const path = ReactEditor.findPath(editor, element);
    const range = Editor.range(editor, path);
    if (Range.start(range).offset === 0) {
      Transforms.insertFragment(
        editor,
        [
          {
            text: '',
          },
        ],
        { at: range },
      );
    } else {
      Transforms.unwrapNodes(editor, { at: range, split: true, mode: 'all' });
    }
    insertBlockLink(elementUrl, editor, context);
  }, [context, editor, elementUrl, element]);

  if (ReactEditor.isReadOnly(editor) || !position) {
    return null;
  }

  const menuAttributes = {
    role: 'listbox',
    'aria-label': 'Link Menu',
    'data-testid': 'hovering-link-menu',
  };

  const { OpenInNew, Fullscreen, CopyLink, Delete } = icons;
  const items = [
    {
      label: 'Open link',
      Icon: OpenInNew,
      secondaryText: clampToLength(elementUrl, elementUrlMaxLengthChars),
      action: () => window.open(elementUrl, '_blank'),
    },
    { label: 'separator-1', separator: true },
    { label: 'Expand block', Icon: Fullscreen, action: expandLink },
    { label: copyLinklabel, Icon: CopyLink, action: copyLink },
    { label: 'Remove link ', Icon: Delete, action: () => removeLink(editor) },
  ];

  const renderMenuItems = ({ getItemProps }: any) =>
    items.map(({ label, Icon, separator, secondaryText, action = () => {} }) =>
      separator ? (
        <Separator key={label} />
      ) : (
        <MenuItem
          key={label}
          size="medium"
          leftDecoration={<Icon style={{ color: theme.color.grey11 }} />}
          primaryText={label}
          secondaryText={secondaryText}
          disableTypography={secondaryText === undefined}
          {...getItemProps({
            item: () => {},
            onClick: action,
          })}
          attributes={{
            'aria-label': label,
          }}
        />
      ),
    );

  return (
    <Menu
      menuListComponent="ul"
      menuListAttributes={menuAttributes}
      renderChildren={renderMenuItems}
      anchorReference="anchorPosition"
      anchorPosition={position}
      above={position.top > 250}
      disableAutoFocus
      css={{ zIndex: 1500 }}
      onClose={onClose}
      // prevent editor from losing selection due to blur event
      onMouseDown={(event: MouseEvent) => event.preventDefault()}
    />
  );
};
